/**
 * Admin Dashboard Page
 * Single-admin control panel: stats, prompt control, user moderation + drill-down, feedback inbox.
 * Gated client-side (redirect) and server-side (/api/admin enforces ADMIN_EMAIL).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';

// Use the theme variables so the page adapts to the active theme (they flip via [data-theme]).
const cell = { padding: '8px 10px', textAlign: 'left' };
const card = { padding: '1rem 1.5rem', border: '1px solid var(--gray-200)', borderRadius: 8, minWidth: 90, background: 'var(--white)' };
const btn = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--gray-300)', cursor: 'pointer', background: 'var(--white)', color: 'var(--gray-800)' };
const dangerBtn = { ...btn, borderColor: 'var(--red-500)', color: 'var(--red-500)' };
const smallBtn = { padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--gray-300)', cursor: 'pointer', background: 'var(--white)', color: 'var(--gray-800)', whiteSpace: 'nowrap' };
const smallDanger = { ...smallBtn, borderColor: 'var(--red-500)', color: 'var(--red-500)' };
const inputStyle = { padding: 6, borderRadius: 6, border: '1px solid var(--gray-300)', background: 'var(--white)', color: 'var(--gray-900)' };
const panel = { flex: 1, minWidth: 280, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12 };

// ---- Launch helpers (templates + UTM table live in LAUNCH.md at the repo root) ----
const promptText = (p) => (p ? [p.emotion, p.connector, p.setting].filter(Boolean).join(' ') : '');
const excerptOf = (content, max = 200) => {
  const clean = String(content || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};
const GROWTH_METRICS = [
  ['signups', 'Signups'],
  ['activationPct', 'Activation (posted <24h)'],
  ['d1ReturnPct', 'D1 return'],
  ['weeklyActiveWriters', 'Active writers'],
  ['posts', 'Posts'],
  ['avgEngagementPerPost', 'Avg engagement/post']
];
const fmtMetric = (key, v) => (v == null ? '—' : key.endsWith('Pct') ? `${v}%` : v);
const Trend = ({ now, prev }) => {
  if (now == null || prev == null || now === prev) return <span style={{ opacity: 0.4 }}>→</span>;
  return now > prev
    ? <span style={{ color: 'var(--green-700)' }}>▲</span>
    : <span style={{ color: 'var(--red-500)' }}>▼</span>;
};

const AdminDashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null); // userId -> { user, posts }
  const [identity, setIdentity] = useState({ username: '', fullName: '', email: '', bio: '' });
  const [userMsg, setUserMsg] = useState('');
  const [promptEdit, setPromptEdit] = useState(null); // in-place edit of today's prompt
  const [imgQuery, setImgQuery] = useState('');        // topic for the image preview
  const [imgResults, setImgResults] = useState([]);    // Unsplash candidates
  const [imgMsg, setImgMsg] = useState('');
  const [pool, setPool] = useState([]);
  const [poolAdd, setPoolAdd] = useState({ basicEmotion: '', longEmotion: '', situation: '' });
  const [crownInput, setCrownInput] = useState('');
  const [crownMessage, setCrownMessage] = useState('');
  const [readPicks, setReadPicks] = useState(null); // /api/admin/read
  const [readInput, setReadInput] = useState('');
  const [readMessage, setReadMessage] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftEdits, setDraftEdits] = useState({}); // draftId -> edited content
  const [draftMessage, setDraftMessage] = useState('');
  const [growth, setGrowth] = useState(null);
  const [contest, setContest] = useState(null); // /api/contest/current (public)
  const [winners, setWinners] = useState([]); // /api/contest/winners (public)
  const [dailyMeta, setDailyMeta] = useState(null); // /api/prompts/daily — prompt + responseCount
  const [channel, setChannel] = useState('x'); // UTM source for X/Bluesky generators
  const [copied, setCopied] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/admin/overview')
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load admin data'))
      .finally(() => setLoading(false));
  }, []);

  const loadPool = useCallback(() => {
    axios.get('/api/admin/prompt-pool').then((res) => setPool(res.data)).catch(() => {});
  }, []);

  const loadDrafts = useCallback(() => {
    // Owner-only. Never call the endpoint otherwise — even a 403 in the network tab would reveal seeding exists.
    if (!user?.isOwner) return;
    axios.get('/api/admin/seed/drafts').then((res) => setDrafts(res.data)).catch(() => {});
  }, [user]);

  const loadLaunch = useCallback(() => {
    axios.get('/api/admin/growth-stats').then((res) => setGrowth(res.data)).catch(() => {});
    axios.get('/api/contest/current').then((res) => setContest(res.data)).catch(() => {});
    axios.get('/api/contest/winners').then((res) => setWinners(res.data)).catch(() => {});
    axios.get('/api/prompts/daily').then((res) => setDailyMeta(res.data)).catch(() => {});
  }, []);

  const loadRead = useCallback(() => {
    axios.get('/api/admin/read').then((res) => setReadPicks(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadPool(); loadDrafts(); loadLaunch(); loadRead(); }, [load, loadPool, loadDrafts, loadLaunch, loadRead]);

  // Seed the prompt-edit form and image-topic box from the loaded daily prompt.
  // Depend on the primitive fields (not the object) so an overview refetch doesn't clobber in-progress edits.
  const dId = data?.dailyPrompt?._id;
  const dEmotion = data?.dailyPrompt?.emotion || '';
  const dConnector = data?.dailyPrompt?.connector || '';
  const dSetting = data?.dailyPrompt?.setting || '';
  useEffect(() => {
    if (!dId) return;
    setPromptEdit({ emotion: dEmotion, connector: dConnector, setting: dSetting });
    setImgQuery(dSetting);
  }, [dId, dEmotion, dConnector, dSetting]);

  if (authLoading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!user.isAdmin) return <Navigate to="/" />;
  const isOwner = !!user.isOwner; // only the owner sees the seeding machinery (fake accounts)

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); load(); } catch (err) { setError(err.response?.data?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const toggleUser = async (id) => {
    if (expanded?.user?._id === id) { setExpanded(null); return; }
    const res = await axios.get(`/api/admin/users/${id}`);
    setExpanded(res.data);
    setIdentity({
      username: res.data.user.username || '',
      fullName: res.data.user.fullName || '',
      email: res.data.user.email || '',
      bio: res.data.user.bio || ''
    });
    setUserMsg('');
  };

  const deleteUser = (u) => {
    if (!window.confirm(`Delete @${u.username} and all their posts? This cannot be undone.`)) return;
    run(() => axios.delete(`/api/admin/users/${u._id}`).then(() => setExpanded(null)));
  };

  const toggleVerify = (u) => {
    run(() => axios.post(`/api/admin/users/${u._id}/verify`));
  };

  const toggleBan = (u) => {
    if (!window.confirm(`${u.isBanned ? 'Unban' : 'Ban'} @${u.username}?`)) return;
    run(() => axios.post(`/api/admin/users/${u._id}/ban`));
  };

  const refreshExpanded = async () => {
    if (expanded) setExpanded((await axios.get(`/api/admin/users/${expanded.user._id}`)).data);
  };

  const saveIdentity = () => {
    run(async () => {
      const res = await axios.patch(`/api/admin/users/${expanded.user._id}`, identity);
      setUserMsg(res.data.message);
      await refreshExpanded();
    });
  };

  const toggleFeature = (postId) => {
    run(() => axios.post(`/api/admin/posts/${postId}/feature`).then(refreshExpanded));
  };

  const dismissReports = (postId) => {
    if (!window.confirm('Clear all reports on this post (the post stays)?')) return;
    run(() => axios.post(`/api/admin/posts/${postId}/dismiss-reports`));
  };

  const deletePost = (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    run(() => axios.delete(`/api/admin/posts/${postId}`).then(refreshExpanded));
  };

  const refreshPrompt = () => {
    if (!window.confirm("Replace today's prompt with a new random one?")) return;
    run(() => axios.post('/api/admin/prompts/refresh'));
  };

  const savePromptEdits = (e) => {
    e.preventDefault();
    run(() => axios.patch('/api/admin/prompts/current', promptEdit));
  };

  // Reroll the current prompt's background image using its own setting (on-theme).
  const rerollImage = () => {
    setImgMsg('');
    run(() => axios.post('/api/admin/prompts/image', {}).then(() => { setImgResults([]); setImgMsg('Image rerolled on-theme.'); }));
  };

  // Preview what Unsplash returns for a topic (defaults to the prompt's setting).
  const previewImages = () => {
    const query = imgQuery.trim();
    setImgMsg('Searching…');
    axios.get('/api/admin/image-preview', { params: query ? { query } : {} })
      .then((res) => { setImgResults(res.data.results); setImgMsg(res.data.results.length ? '' : 'No images for that topic.'); })
      .catch((err) => setImgMsg(err.response?.data?.message || 'Preview failed.'));
  };

  // Pin one previewed image as today's background.
  const pickImage = (url) => {
    run(() => axios.post('/api/admin/prompts/image', { url }).then(() => { setImgResults([]); setImgMsg('Background image set.'); }));
  };

  const addPoolItem = (kind) => {
    const text = poolAdd[kind].trim();
    if (!text) return;
    run(() => axios.post('/api/admin/prompt-pool', { kind, text })
      .then(() => { setPoolAdd((p) => ({ ...p, [kind]: '' })); loadPool(); }));
  };

  const togglePoolItem = (i) => {
    run(() => axios.patch(`/api/admin/prompt-pool/${i._id}`, { active: i.active === false }).then(loadPool));
  };

  const removePoolItem = (id) => {
    run(() => axios.delete(`/api/admin/prompt-pool/${id}`).then(loadPool));
  };

  const saveDraftEdit = (d) => {
    const content = draftEdits[d._id];
    if (content == null || content === d.content) return;
    run(() => axios.patch(`/api/admin/seed/drafts/${d._id}`, { content })
      .then(() => { setDraftMessage('Draft updated.'); loadDrafts(); }));
  };

  const approveDraft = (d) => {
    run(async () => {
      // If there's an unsaved edit in the textarea, save it first — the server
      // publishes the draft's stored content.
      const edited = draftEdits[d._id];
      if (edited != null && edited !== d.content) {
        await axios.patch(`/api/admin/seed/drafts/${d._id}`, { content: edited });
      }
      const res = await axios.post(`/api/admin/seed/drafts/${d._id}/approve`);
      setDraftMessage(res.data.message);
      loadDrafts();
    });
  };

  const rejectDraft = (d) => {
    if (!window.confirm(`Reject this draft by ${d.personaEmail}?`)) return;
    run(() => axios.post(`/api/admin/seed/drafts/${d._id}/reject`)
      .then((res) => { setDraftMessage(res.data.message); loadDrafts(); }));
  };

  const crownWinner = (e) => {
    e.preventDefault();
    // Accepts a bare post id or a pasted /post/<id> link.
    const match = crownInput.match(/[0-9a-fA-F]{24}/);
    if (!match) {
      setCrownMessage('Paste a post link or 24-character post id.');
      return;
    }
    setCrownMessage('');
    run(() => axios.post('/api/admin/contest/crown', { postId: match[0] })
      .then((res) => { setCrownMessage(res.data.message); setCrownInput(''); }));
  };

  const queueRead = (e) => {
    e.preventDefault();
    const match = readInput.match(/[0-9a-fA-F]{24}/);
    if (!match) {
      setReadMessage('Paste a post link or 24-character post id.');
      return;
    }
    setReadMessage('');
    run(() => axios.post('/api/admin/read/queue', { postId: match[0] })
      .then((res) => { setReadMessage(res.data.message); setReadInput(''); loadRead(); }));
  };

  const removeRead = (id) => {
    if (!window.confirm('Remove this Read pick?')) return;
    run(() => axios.delete(`/api/admin/read/${id}`).then(loadRead));
  };

  const copyText = (key, text) => {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(key); setTimeout(() => setCopied(''), 2000); })
      .catch(() => window.alert('Copy failed — select the text and copy manually.'));
  };

  // ---- Launch derivations ----
  const isMondayUK = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short' }).format(new Date()) === 'Mon';
  const msLeft = contest ? Math.max(0, new Date(contest.weekEnd).getTime() - Date.now()) : 0;
  const contestCountdown = `${Math.floor(msLeft / 86400000)}d ${Math.floor((msLeft % 86400000) / 3600000)}h`;
  // Last week's key, same shape the server uses (UK-date of that week's Monday).
  const lastWeekKey = contest
    ? new Date(new Date(contest.weekStart).getTime() - 7 * 86400000).toLocaleDateString('en-CA', { timeZone: 'Europe/London' })
    : null;
  const lastWeekCrowned = winners.some((w) => w.weekKey === lastWeekKey);
  const latestWinner = winners.find((w) => w.post && w.post.author); // newest first; skip deleted posts

  const todayPromptText = promptText(dailyMeta || data?.dailyPrompt);
  const prize = contest?.prizeAmount || '£10';
  const generators = [
    {
      key: 'daily',
      title: `Daily post (${channel === 'x' ? 'X' : 'Bluesky'})`,
      text: `Today's WriteFlow prompt: "${todayPromptText}"

[YOUR 100 WORDS HERE — human-written, always]

One prompt a day, 100/250/500 words. Best of the week wins ${prize} → https://writeflow.ink/?utm_source=${channel}&utm_campaign=daily

#vss365 #WritingCommunity`
    },
    {
      key: 'winner',
      title: `Winner announcement (${channel === 'x' ? 'X' : 'Bluesky'})`,
      disabled: !latestWinner,
      text: latestWinner
        ? `This week's ${prize} WriteFlow contest winner: @${latestWinner.post.author.username} 🏆

Prompt was "${promptText(latestWinner.post.prompt)}" — here's the winning entry:

"${excerptOf(latestWinner.post.content)}"

New prompt every day, new contest every Monday → https://writeflow.ink/contest?utm_source=${channel}&utm_campaign=winner`
        : 'No contest winner crowned yet.'
    },
    {
      key: 'discord',
      title: 'Discord pitch',
      text: `Made a small thing for daily-writing-habit people: https://writeflow.ink/?utm_source=discord&utm_campaign=pitch — one weird prompt a day (today: "${todayPromptText}"), you answer in exactly 100, 250 or 500 words. Every response auto-enters a weekly contest, best one wins ${prize}. Free, no AI-generated content, just a warm-up ritual. Would love brutal feedback.`
    }
  ];

  return (
    <div className="admin-page">
      <h1>Admin</h1>
      {error && <p style={{ color: 'var(--red-500)' }}>{error}</p>}
      {loading && <div className="loading">Loading...</div>}

      {data && (
        <>
          {/* Stats */}
          <div style={{ display: 'flex', gap: '1rem', margin: '1rem 0', flexWrap: 'wrap' }}>
            {Object.entries(data.counts).map(([key, value]) => (
              <div key={key} style={card}>
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{value}</div>
                <div style={{ textTransform: 'capitalize', opacity: 0.7 }}>{key}</div>
              </div>
            ))}
          </div>

          {/* Launch — the daily/weekly growth operating rhythm (playbook: LAUNCH.md) */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Launch</h2>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>

              {/* Growth stats: this week vs last */}
              <div style={panel}>
                <strong>Growth — last 7 days vs prior 7</strong>
                {!growth ? <p style={{ opacity: 0.6 }}>Loading…</p> : (
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--gray-200)', fontSize: 13 }}>
                        <th style={cell}>Metric</th><th style={cell}>This wk</th><th style={cell}>Last wk</th><th style={cell}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {GROWTH_METRICS.map(([key, label]) => (
                        <tr key={key} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                          <td style={{ ...cell, fontSize: 13 }}>{label}</td>
                          <td style={{ ...cell, fontWeight: 700 }}>{fmtMetric(key, growth.thisWeek[key])}</td>
                          <td style={{ ...cell, opacity: 0.7 }}>{fmtMetric(key, growth.prevWeek[key])}</td>
                          <td style={cell}><Trend now={growth.thisWeek[key]} prev={growth.prevWeek[key]} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {isOwner && <p style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>Seed accounts excluded.</p>}
              </div>

              {/* Ops checklist — all derived from live data */}
              <div style={panel}>
                <strong>Today's ops</strong>
                <ul style={{ paddingLeft: 20, margin: '8px 0', lineHeight: 1.9 }}>
                  <li>
                    ✍️ Today's prompt: {dailyMeta
                      ? <>"{todayPromptText}" — <strong>{dailyMeta.responseCount}</strong> response(s){dailyMeta.responseCount === 0 && ' — none yet, write yours'}</>
                      : '…'}
                  </li>
                  {isOwner && <li>🌱 Seed drafts pending: <strong>{drafts.length}</strong>{drafts.length > 0 && ' — review below'}</li>}
                  <li>🏆 Contest week ends in <strong>{contest ? contestCountdown : '…'}</strong> (prize {prize})</li>
                  <li>
                    👑 {isMondayUK
                      ? (lastWeekCrowned
                        ? <>It's Monday — last week's winner is crowned ✅</>
                        : <strong style={{ color: 'var(--red-500)' }}>It's Monday — crown last week's winner today (Weekly contest section below)</strong>)
                      : (lastWeekCrowned
                        ? <>Last week's winner crowned ✅ — next crowning Monday</>
                        : <span style={{ color: 'var(--red-500)' }}>Last week's winner not crowned yet</span>)}
                  </li>
                </ul>
                <strong style={{ fontSize: 13 }}>This week's shortlist (by likes)</strong>
                {!contest || contest.leaderboard.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.6 }}>No entries yet this week.</p>
                ) : contest.leaderboard.slice(0, 5).map((p, i) => (
                  <div key={p._id} style={{ fontSize: 13, borderTop: '1px solid var(--gray-200)', padding: '6px 0' }}>
                    {i + 1}. @{p.author?.username || 'unknown'} · ♥ {p.likeCount}
                    <div style={{ opacity: 0.6 }}>{excerptOf(p.excerpt, 90)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Copy-paste social post generators (templates + UTM links from LAUNCH.md) */}
            <div style={{ marginTop: 16 }}>
              <strong>Post generators</strong>
              <label style={{ fontSize: 13, marginLeft: 12 }}>Channel:{' '}
                <select value={channel} onChange={(e) => setChannel(e.target.value)} style={inputStyle}>
                  <option value="x">X</option>
                  <option value="bluesky">Bluesky</option>
                </select>
              </label>
              <p style={{ fontSize: 13, opacity: 0.7, margin: '4px 0 0' }}>
                Prefilled with live data + the right UTM link. The [YOUR 100 WORDS HERE] placeholder stays
                human-written — we market "no AI content".
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                {generators.map((g) => (
                  <div key={g.key} style={panel}>
                    <strong style={{ fontSize: 14 }}>{g.title}</strong>
                    <textarea readOnly rows={9} value={g.text}
                      style={{ ...inputStyle, width: '100%', margin: '8px 0', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                    <button style={btn} disabled={!!g.disabled} onClick={() => copyText(g.key, g.text)}>
                      {copied === g.key ? 'Copied ✓' : '📋 Copy'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Prompt control */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Today's prompt</h2>
            {!data.dailyPrompt ? <p>No daily prompt set.</p> : (
              <>
                {/* Edit the current prompt in place (keeps id, date and its responses) */}
                <form onSubmit={savePromptEdits} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input required placeholder="emotion" value={promptEdit?.emotion ?? ''}
                    onChange={(e) => setPromptEdit((p) => ({ ...p, emotion: e.target.value }))} style={inputStyle} />
                  <input required placeholder="connector (while/at/during)" value={promptEdit?.connector ?? ''}
                    onChange={(e) => setPromptEdit((p) => ({ ...p, connector: e.target.value }))} style={{ ...inputStyle, width: 120 }} />
                  <input required placeholder="setting" value={promptEdit?.setting ?? ''}
                    onChange={(e) => setPromptEdit((p) => ({ ...p, setting: e.target.value }))} style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
                  <button type="submit" style={btn} disabled={busy}>Save edits</button>
                  <button type="button" style={btn} onClick={refreshPrompt} disabled={busy}>🎲 New random prompt</button>
                </form>

                {/* Background image control */}
                <div style={{ ...panel, marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>Current background</div>
                      {data.dailyPrompt.imageUrl
                        ? <img src={data.dailyPrompt.imageUrl} alt="" style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)' }} />
                        : <div style={{ width: 160, height: 90, borderRadius: 6, border: '1px dashed var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, opacity: 0.6 }}>none</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input placeholder="image topic (defaults to setting)" value={imgQuery}
                          onChange={(e) => setImgQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); previewImages(); } }}
                          style={{ ...inputStyle, flex: 1, minWidth: 180 }} />
                        <button type="button" style={btn} onClick={previewImages} disabled={busy}>🔍 Preview topic</button>
                        <button type="button" style={btn} onClick={rerollImage} disabled={busy}>🎲 Reroll on-theme</button>
                      </div>
                      {imgMsg && <p style={{ fontSize: 13, marginTop: 6 }}>{imgMsg}</p>}
                      {imgResults.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                          {imgResults.map((r) => (
                            <img key={r.url} src={r.thumb} alt="" title={`Set this image${r.credit ? ` · © ${r.credit}` : ''}`}
                              onClick={() => !busy && pickImage(r.url)}
                              style={{ width: 104, height: 68, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--gray-200)' }} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Contest */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Weekly contest</h2>
            <p style={{ opacity: 0.7 }}>
              Crown a post as its week's winner (the week comes from when the post was written).
              Re-crowning a week demotes the previous winner.
            </p>
            <form onSubmit={crownWinner} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                required
                placeholder="Post link or id"
                value={crownInput}
                onChange={(e) => setCrownInput(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 220 }}
              />
              <button type="submit" style={btn} disabled={busy}>🏆 Crown winner</button>
            </form>
            {crownMessage && <p style={{ marginTop: 8 }}>{crownMessage}</p>}
          </section>

          {/* Read */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Read</h2>
            <p style={{ opacity: 0.7 }}>
              Hand-pick a post for the home page's Read tab. Your picks go first; if you haven't picked one by 6pm UK,
              the latest contest winner (then the most-liked recent response) is picked automatically.
              The author is asked to add notes and the piece shows {readPicks?.noticeDays ?? 2} days after it's picked,
              once it has at least one note. Days with nothing ready, and every Sunday, show a classic instead.
            </p>
            <form onSubmit={queueRead} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                required
                placeholder="Post link or id"
                value={readInput}
                onChange={(e) => setReadInput(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 220 }}
              />
              <button type="submit" style={btn} disabled={busy}>📖 Pick for Read</button>
            </form>
            {readMessage && <p style={{ marginTop: 8 }}>{readMessage}</p>}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, minWidth: 560 }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={cell}>Day</th><th style={cell}>Author</th><th style={cell}>Piece</th><th style={cell}>How</th><th style={cell}>Status</th><th style={cell}>Notes</th><th style={cell}>Hearts</th><th style={cell} />
                  </tr>
                </thead>
                <tbody>
                  {(readPicks?.pieces || []).map((p) => (
                    <tr key={p._id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                      <td style={cell}>{p.shownOn || p.showOn || 'next free'}</td>
                      <td style={cell}>@{p.username}</td>
                      <td style={cell}>{p.postId ? <Link to={`/read/${p.postId}`}>{p.excerpt}</Link> : p.excerpt}</td>
                      <td style={cell}>{p.source}</td>
                      <td style={cell}>{p.shownOn ? 'shown' : p.status}</td>
                      <td style={cell}>{p.notes}</td>
                      <td style={cell}>{p.hearts}</td>
                      <td style={cell}>
                        <button style={smallBtn} disabled={busy || !!p.shownOn} onClick={() => removeRead(p._id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {readPicks && readPicks.pieces.length === 0 && <p>No picks yet.</p>}
          </section>

          {/* Seed drafts — owner only (the fake-account machinery is hidden from plain admins) */}
          {isOwner && (
          <section style={{ margin: '2rem 0' }}>
            <h2>Seed drafts ({drafts.length} pending)</h2>
            <p style={{ fontSize: 13, opacity: 0.7 }}>
              Draft prompt responses submitted by the external generator. Edit if needed,
              then approve to publish as the persona's post, or reject.
            </p>
            {draftMessage && <p style={{ marginTop: 8 }}>{draftMessage}</p>}
            {drafts.length === 0 ? <p>No pending drafts.</p> : drafts.map((d) => (
              <div key={d._id} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  {d.personaEmail} · {d.wordCategory}w · {new Date(d.createdAt).toLocaleString()}
                  {d.prompt && <> · ↳ {d.prompt.emotion} {d.prompt.connector} {d.prompt.setting}</>}
                </div>
                <textarea
                  rows={6}
                  value={draftEdits[d._id] ?? d.content}
                  onChange={(e) => setDraftEdits((p) => ({ ...p, [d._id]: e.target.value }))}
                  style={{ ...inputStyle, width: '100%', marginTop: 8, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <button style={btn} disabled={busy || (draftEdits[d._id] ?? d.content) === d.content}
                    onClick={() => saveDraftEdit(d)}>Save edit</button>
                  <button style={btn} disabled={busy} onClick={() => approveDraft(d)}>Approve</button>
                  <button style={dangerBtn} disabled={busy} onClick={() => rejectDraft(d)}>Reject</button>
                </div>
              </div>
            ))}
          </section>
          )}

          {/* Prompt library */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Prompt library ({pool.length})</h2>
            <p style={{ fontSize: 13, opacity: 0.7 }}>
              The pool the daily generator samples from. Changes take effect on the next prompt.
              Keep it simple: an emotion at a place ("Furious at the park"). Situations should start with at / in / on.
              Switched-off items stay listed (greyed) but are never picked.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[['basicEmotion', 'Basic emotions'], ['longEmotion', 'Long emotions'], ['situation', 'Situations']].map(([kind, label]) => {
                const items = pool.filter((i) => i.kind === kind).sort((a, b) => (a.active === false) - (b.active === false));
                const onCount = items.filter((i) => i.active !== false).length;
                return (
                  <div key={kind} style={{ flex: 1, minWidth: 220, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12 }}>
                    <strong>{label} ({onCount} on / {items.length})</strong>
                    <div style={{ maxHeight: 240, overflowY: 'auto', margin: '8px 0' }}>
                      {items.map((i) => (
                        <div key={i._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
                          <span style={{ fontSize: 13, opacity: i.active === false ? 0.4 : 1 }}>{i.text}</span>
                          <span style={{ display: 'flex', gap: 4 }}>
                          <button title={i.active === false ? 'Switch on' : 'Switch off'} onClick={() => togglePoolItem(i)} disabled={busy}
                            style={{ ...btn, padding: '0 6px', width: 34 }}>{i.active === false ? 'off' : 'on'}</button>
                          <button title="Remove" onClick={() => removePoolItem(i._id)} disabled={busy}
                            style={{ ...btn, padding: '0 6px', color: 'var(--red-500)', borderColor: 'var(--red-500)' }}>×</button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input placeholder={`add ${label.toLowerCase()}`} value={poolAdd[kind]}
                        onChange={(e) => setPoolAdd((p) => ({ ...p, [kind]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') addPoolItem(kind); }}
                        style={{ ...inputStyle, flex: 1 }} />
                      <button style={btn} disabled={busy} onClick={() => addPoolItem(kind)}>Add</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Users */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Users ({data.users.length})</h2>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                  <th style={cell}>Username</th><th style={cell}>Email</th><th style={cell}>Name</th>
                  <th style={cell}>Posts</th><th style={cell}>Followers</th><th style={cell}>Joined</th><th style={cell}></th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <React.Fragment key={u._id}>
                    <tr style={{ borderBottom: '1px solid var(--gray-200)', cursor: 'pointer', opacity: u.isBanned ? 0.55 : 1 }} onClick={() => toggleUser(u._id)}>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>@{u.username}{u.isVerified ? ' ✔' : ''}{u.isBanned ? ' 🚫' : ''}</td>
                      <td style={{ ...cell, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email}</td>
                      <td style={cell}>{u.fullName || '—'}</td>
                      <td style={cell}>{u.postCount}</td>
                      <td style={cell}>{u.followers?.length || 0}</td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <button style={{ ...smallBtn, marginRight: 4 }} disabled={busy}
                          onClick={(e) => { e.stopPropagation(); toggleVerify(u); }}>
                          {u.isVerified ? 'Unverify' : 'Verify'}
                        </button>
                        <button style={{ ...smallBtn, marginRight: 4 }} disabled={busy}
                          onClick={(e) => { e.stopPropagation(); toggleBan(u); }}>
                          {u.isBanned ? 'Unban' : 'Ban'}
                        </button>
                        <button style={smallDanger} disabled={busy}
                          onClick={(e) => { e.stopPropagation(); deleteUser(u); }}>Delete</button>
                      </td>
                    </tr>
                    {expanded?.user?._id === u._id && (
                      <tr><td colSpan={7} style={{ background: 'var(--gray-100)', padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                          <label style={{ fontSize: 12 }}>Username<br />
                            <input value={identity.username}
                              onChange={(e) => setIdentity((s) => ({ ...s, username: e.target.value }))}
                              style={inputStyle} />
                          </label>
                          <label style={{ fontSize: 12 }}>Name<br />
                            <input value={identity.fullName}
                              onChange={(e) => setIdentity((s) => ({ ...s, fullName: e.target.value }))}
                              style={inputStyle} />
                          </label>
                          <label style={{ fontSize: 12 }}>Email{u.isSeed ? ' (seed — locked)' : ''}<br />
                            <input value={identity.email} disabled={u.isSeed}
                              title={u.isSeed ? 'A seed persona email is the internal seeding key and can’t be changed.' : ''}
                              onChange={(e) => setIdentity((s) => ({ ...s, email: e.target.value }))}
                              style={{ ...inputStyle, ...(u.isSeed ? { opacity: 0.5 } : {}) }} />
                          </label>
                          <button style={btn} disabled={busy} onClick={saveIdentity}>Save identity</button>
                          {userMsg && <span style={{ fontSize: 12, opacity: 0.7 }}>{userMsg}</span>}
                        </div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>Bio<br />
                          <textarea value={identity.bio} rows={3} maxLength={500}
                            onChange={(e) => setIdentity((s) => ({ ...s, bio: e.target.value }))}
                            style={{ ...inputStyle, width: '100%', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                        </label>
                        <strong>{expanded.posts.length} post(s) by @{u.username}</strong>
                        {expanded.posts.length === 0 ? <p>No posts.</p> : expanded.posts.map((p) => (
                          <div key={p._id} style={{ borderTop: '1px solid var(--gray-200)', padding: '8px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              {p.prompt && <div style={{ fontSize: 12, opacity: 0.6 }}>↳ {p.prompt.emotion} {p.prompt.connector} {p.prompt.setting}</div>}
                              <div style={{ whiteSpace: 'pre-wrap' }}>{p.isFeatured ? '⭐ ' : ''}{p.content.length > 240 ? p.content.slice(0, 240) + '…' : p.content}</div>
                              <div style={{ fontSize: 12, opacity: 0.6 }}>
                                {new Date(p.createdAt).toLocaleDateString()} · ♥ {p.likeCount} · 💬 {p.commentCount} · {p.wordCategory}w
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                              <button style={btn} disabled={busy} onClick={() => toggleFeature(p._id)}>
                                {p.isFeatured ? 'Unfeature' : 'Feature'}
                              </button>
                              <button style={dangerBtn} disabled={busy} onClick={() => deletePost(p._id)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
          </section>

          {/* Reported posts */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Reported posts ({data.reportedPosts?.length || 0})</h2>
            {(!data.reportedPosts || data.reportedPosts.length === 0) ? (
              <p>No reported posts.</p>
            ) : data.reportedPosts.map((p) => (
              <div key={p._id} style={{ borderBottom: '1px solid var(--gray-200)', padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>by @{p.author?.username || 'unknown'}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {(p.content || '').length > 240 ? p.content.slice(0, 240) + '…' : (p.content || '[repost]')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--red-500)' }}>
                    🚩 {p.reports.length} report(s): {p.reports.map((r) => r.reason || '(no reason)').join('; ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <button style={btn} disabled={busy} onClick={() => dismissReports(p._id)}>Dismiss</button>
                  <button style={dangerBtn} disabled={busy} onClick={() => deletePost(p._id)}>Delete</button>
                </div>
              </div>
            ))}
          </section>

          {/* Feedback */}
          <section style={{ margin: '2rem 0' }}>
            <h2>Feedback ({data.feedback.length})</h2>
            {data.feedback.length === 0 ? <p>No feedback yet.</p> : data.feedback.map((f) => (
              <div key={f._id} style={{ borderBottom: '1px solid var(--gray-200)', padding: '10px 0' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{f.content}</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  {f.user ? `@${f.user.username}` : 'anonymous'} · {new Date(f.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;
